import React from 'react'
import {
  useGetMilestonesQuery,
  useGetMilestonesByListIdQuery,
} from '@/redux/service/milestone'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CalendarDays,
  Clock,
  User2,
  AlertCircle,
  ListTodo,
  Calendar,
  ChevronRight,
  ChevronDown,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { formatSeconds, dateFormat } from '@/lib/utils'
import Tag from '@/lib/Tag'
import { useCheckPermission } from '@/hooks/useCheckPermission'

interface Milestone {
  id: string
  name: string
  description: string | null
  list_id: number
  status: string
  date_created: string | null
  date_updated: string | null
  date_done: string | null
  priority: string | null
  due_date: string | null
  start_date: string | null
  time_spent: number | null
  time_estimate: number | null
  assignee_names: string[]
}

interface MilestonesProps {
  projectId: number
}

/** Deterministic hue from name for theme-aware avatar colors */
const nameToHue = (name: string) => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % 360
}

/** Helper to format currency values. Returns em-dash '—' if value is 0, null, or undefined. */
const formatCurrencyWithDash = (
  value: number | string | null | undefined
): string => {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (isNaN(num) || num === 0) return '—'
  return `$${num.toFixed(2)}`
}

const thClass =
  'text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'

const Milestones: React.FC<MilestonesProps> = ({ projectId }) => {
  const { data, isLoading, isError } = useGetMilestonesQuery(projectId)
  const { data: detailData } = useGetMilestonesByListIdQuery(
    projectId.toString()
  )

  const [expandedRow, setExpandedRow] = React.useState<string | null>(null)
  const { hasPageAccess } = useCheckPermission()

  const milestones: Milestone[] = data?.data || []
  const milestoneDetails = detailData?.data || []

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className='border border-border rounded-xl bg-card shadow-sm overflow-hidden'>
        <div className='flex items-center gap-2 px-4 py-3 bg-muted/40 border-b border-border'>
          <Skeleton className='h-3.5 w-3.5 rounded' />
          <Skeleton className='h-3.5 w-36 rounded' />
        </div>
        <div className='divide-y divide-border/50 p-4 space-y-3'>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className='h-12 w-full rounded-lg' />
          ))}
        </div>
      </div>
    )
  }

  /* ── Error ── */
  if (isError) {
    return (
      <div className='border border-destructive/20 bg-destructive/5 rounded-xl p-8 text-center'>
        <AlertCircle className='mx-auto mb-3 h-7 w-7 text-destructive/70' />
        <p className='text-sm font-semibold text-destructive'>
          Failed to load milestones
        </p>
        <p className='text-xs text-muted-foreground mt-1'>
          Please try again later
        </p>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className='space-y-4'
    >
      {/* ── Header ── */}
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20'>
          <ListTodo className='w-4 h-4 text-primary' />
          <span className='text-sm font-semibold text-primary'>
            Project Milestones
          </span>
        </div>
        <span className='text-xs font-medium text-muted-foreground tabular-nums'>
          {milestones.length}{' '}
          {milestones.length === 1 ? 'milestone' : 'milestones'}
        </span>
      </div>

      {/* ── Table card ── */}
      <div className='border border-border rounded-sm bg-card shadow-sm overflow-hidden'>
        <div className='max-h-[560px] overflow-y-auto overflow-x-auto'>
          <Table className='border-none'>
            <TableHeader>
              <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
                {/* Expand toggle col */}
                <TableHead className='w-10 px-3 sticky left-0 z-30 bg-muted/40' />
                <TableHead
                  className={`${thClass} py-3 w-[320px] sticky left-10 z-20 bg-muted/40 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]`}
                >
                  Milestone
                </TableHead>
                <TableHead className={thClass}>Assignees</TableHead>
                <TableHead className={thClass}>Status</TableHead>
                <TableHead className={thClass}>Start Date</TableHead>
                <TableHead className={thClass}>Due Date</TableHead>
                <TableHead className={thClass}>Estimated</TableHead>
                <TableHead className={thClass}>Tracked</TableHead>
                <TableHead className={thClass}>Remaining</TableHead>
                <TableHead className={thClass}>Priority</TableHead>
                <TableHead className={thClass}>Created</TableHead>
                <TableHead className={thClass}>Completed</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              <AnimatePresence mode='popLayout'>
                {milestones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className='h-32 text-center'>
                      <div className='flex flex-col items-center gap-1.5'>
                        <ListTodo className='h-7 w-7 text-muted-foreground/30' />
                        <p className='text-sm font-medium text-muted-foreground'>
                          No milestones found
                        </p>
                        <p className='text-xs text-muted-foreground/60'>
                          Milestones for this project will appear here
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  milestones.map((milestone, index) => {
                    const details = milestoneDetails.find(
                      m => m.milestone_id === milestone.id
                    )
                    const isExpanded = expandedRow === milestone.id

                    return (
                      <React.Fragment key={milestone.id}>
                        {/* ── Main row ── */}
                        <motion.tr
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.04, duration: 0.25 }}
                          className='group border-b border-border/50 hover:bg-accent/30 transition-colors duration-150 cursor-pointer'
                          onClick={() =>
                            setExpandedRow(isExpanded ? null : milestone.id)
                          }
                        >
                          {/* Expand button */}
                          <TableCell className='px-3 sticky left-0 z-10 bg-card group-hover:bg-accent/30 transition-colors'>
                            <button
                              onClick={() =>
                                setExpandedRow(isExpanded ? null : milestone.id)
                              }
                              className='p-1 rounded-md hover:bg-border transition-colors'
                            >
                              {isExpanded ? (
                                <ChevronDown className='h-3.5 w-3.5 text-primary' />
                              ) : (
                                <ChevronRight className='h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors' />
                              )}
                            </button>
                          </TableCell>

                          {/* Name */}
                          <TableCell className='px-4 py-3 sticky left-10 z-10 bg-card group-hover:bg-accent/30 transition-colors shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]'>
                            <div className='flex flex-col gap-0.5 max-w-[300px]'>
                              <span
                                className='text-sm font-semibold truncate'
                                title={milestone.name}
                              >
                                {milestone.name}
                              </span>
                              {milestone.description && (
                                <span className='text-xs text-muted-foreground line-clamp-1'>
                                  {milestone.description}
                                </span>
                              )}
                            </div>
                          </TableCell>

                          {/* Assignees */}
                          <TableCell className='px-4 py-3'>
                            <div className='flex -space-x-1.5'>
                              {milestone.assignee_names.length > 0 ? (
                                milestone.assignee_names.map((name, i) => {
                                  const hue = nameToHue(name ?? '')
                                  return (
                                    <div
                                      key={i}
                                      className='h-6 w-6 rounded-full border-2 border-background flex items-center justify-center text-[9px] font-bold shrink-0'
                                      style={{
                                        backgroundColor: `hsl(${hue} 55% 88%)`,
                                        color: `hsl(${hue} 55% 32%)`,
                                      }}
                                      title={name}
                                    >
                                      {name !== null ? (
                                        name
                                          ?.split(' ')
                                          ?.map(n => n[0])
                                          ?.join('')
                                      ) : (
                                        <User2 className='h-3 w-3' />
                                      )}
                                    </div>
                                  )
                                })
                              ) : (
                                <div className='h-6 w-6 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground'>
                                  <User2 className='h-3 w-3' />
                                </div>
                              )}
                            </div>
                          </TableCell>

                          {/* Status */}
                          <TableCell className='px-4 py-3 truncate  '>
                            <Tag text={milestone.status} />
                          </TableCell>

                          {/* Start Date */}
                          <TableCell className='px-4 py-3'>
                            <div className='flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap'>
                              <Calendar className='h-3 w-3 shrink-0' />
                              {milestone.start_date
                                ? dateFormat(milestone.start_date)
                                : '—'}
                            </div>
                          </TableCell>

                          {/* Due Date */}
                          <TableCell className='px-4 py-3'>
                            <div className='flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap'>
                              <Calendar className='h-3 w-3 shrink-0' />
                              {milestone.due_date
                                ? dateFormat(milestone.due_date)
                                : '—'}
                            </div>
                          </TableCell>

                          {/* Time Estimate */}
                          <TableCell className='px-4 py-3'>
                            <div className='flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground'>
                              <Clock className='h-3 w-3 shrink-0' />
                              {milestone.time_estimate
                                ? formatSeconds(
                                    milestone.time_estimate / 1000
                                  ) + 'h'
                                : '—'}
                            </div>
                          </TableCell>

                          {/* Time Tracked */}
                          <TableCell className='px-4 py-3'>
                            <div className='flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground'>
                              <Clock className='h-3 w-3 shrink-0' />
                              {milestone.time_spent
                                ? formatSeconds(milestone.time_spent / 1000) +
                                  'h'
                                : '—'}
                            </div>
                          </TableCell>

                          {/* Remaining Hours */}
                          <TableCell className='px-4 py-3'>
                            {(() => {
                              const estimateSeconds =
                                (milestone.time_estimate ?? 0) / 1000
                              const trackedSeconds =
                                (milestone.time_spent ?? 0) / 1000
                              const remainingSeconds =
                                estimateSeconds - trackedSeconds

                              if (!milestone.time_estimate) {
                                return (
                                  <span className='text-muted-foreground text-xs'>
                                    —
                                  </span>
                                )
                              }

                              return (
                                <div
                                  className={`flex items-center gap-1.5 text-xs tabular-nums font-medium ${
                                    remainingSeconds < 0
                                      ? 'text-destructive'
                                      : 'text-muted-foreground'
                                  }`}
                                >
                                  <Clock className='h-3 w-3 shrink-0' />
                                  {remainingSeconds < 0 ? '-' : ''}
                                  {formatSeconds(Math.abs(remainingSeconds))}h
                                </div>
                              )
                            })()}
                          </TableCell>

                          {/* Priority */}
                          <TableCell className='px-4 py-3'>
                            {milestone.priority ? (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
                                  milestone.priority.toLowerCase() === 'urgent'
                                    ? 'bg-destructive/10 text-destructive border-destructive/20'
                                    : milestone.priority.toLowerCase() ===
                                        'high'
                                      ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20'
                                      : milestone.priority.toLowerCase() ===
                                          'normal'
                                        ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20'
                                        : 'bg-muted text-muted-foreground border-border'
                                }`}
                              >
                                {milestone.priority}
                              </span>
                            ) : (
                              <span className='text-muted-foreground text-xs'>
                                —
                              </span>
                            )}
                          </TableCell>

                          {/* Created Date */}
                          <TableCell className='px-4 py-3'>
                            <div className='flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap'>
                              <CalendarDays className='h-3 w-3 shrink-0' />
                              {milestone.date_created
                                ? dateFormat(milestone.date_created)
                                : '—'}
                            </div>
                          </TableCell>

                          {/* Completed Date */}
                          <TableCell className='px-4 py-3'>
                            <div className='flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap'>
                              <CalendarDays className='h-3 w-3 shrink-0' />
                              {milestone.date_done
                                ? dateFormat(milestone.date_done)
                                : '—'}
                            </div>
                          </TableCell>
                        </motion.tr>

                        {/* ── Expanded detail row ── */}
                        {isExpanded && (
                          <TableRow className='border-b border-border/50 bg-muted/20'>
                            <TableCell colSpan={12} className='px-6 py-4'>
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                transition={{ duration: 0.2 }}
                                className='space-y-3'
                              >
                                {/* Sub-table header */}
                                <div className='flex items-center gap-2'>
                                  <span className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                                    Resource Allocation
                                  </span>
                                </div>

                                <div className='border border-border rounded-lg bg-card overflow-hidden shadow-sm'>
                                  <Table>
                                    <TableHeader>
                                      <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
                                        <TableHead
                                          className={`${thClass} py-2.5`}
                                        >
                                          Employee
                                        </TableHead>
                                        <TableHead
                                          className={`${thClass} text-center`}
                                        >
                                          Time Tracked
                                        </TableHead>
                                        {hasPageAccess('finance') && (
                                          <>
                                            <TableHead
                                              className={`${thClass} text-center`}
                                            >
                                              Employee Cost
                                            </TableHead>
                                            <TableHead
                                              className={`${thClass} text-center`}
                                            >
                                              Overhead
                                            </TableHead>
                                            <TableHead
                                              className={`${thClass} text-center`}
                                            >
                                              Fully Loaded
                                            </TableHead>
                                          </>
                                        )}
                                        {(hasPageAccess('finance') ||
                                          hasPageAccess('mini_finance')) && (
                                          <TableHead
                                            className={`${thClass} text-center`}
                                          >
                                            Project Amount
                                          </TableHead>
                                        )}
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {details && details.users.length > 0 ? (
                                        details.users.map((user, idx) => {
                                          const hue = nameToHue(
                                            user.user_name ?? ''
                                          )
                                          return (
                                            <TableRow
                                              key={idx}
                                              className='border-b border-border/50 hover:bg-accent/20 transition-colors'
                                            >
                                              {/* Employee */}
                                              <TableCell className='px-4 py-2.5'>
                                                <div className='flex items-center gap-2'>
                                                  <div
                                                    className='h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 border'
                                                    style={{
                                                      backgroundColor: `hsl(${hue} 55% 88%)`,
                                                      color: `hsl(${hue} 55% 32%)`,
                                                      borderColor: `hsl(${hue} 55% 78%)`,
                                                    }}
                                                  >
                                                    {user.user_name
                                                      ?.split(' ')
                                                      .map(n => n[0])
                                                      .join('')}
                                                  </div>
                                                  <span className='text-sm font-medium'>
                                                    {user.user_name}
                                                  </span>
                                                </div>
                                              </TableCell>

                                              {/* Time Tracked */}
                                              <TableCell className='px-4 py-2.5 text-center'>
                                                <div className='text-sm font-medium tabular-nums'>
                                                  {formatSeconds(
                                                    user.duration_seconds
                                                  )}
                                                </div>
                                                <div className='flex flex-col gap-0.5 mt-0.5'>
                                                  <span className='text-[10px] tabular-nums text-emerald-600 dark:text-emerald-400 font-medium'>
                                                    {formatSeconds(
                                                      user.billable_duration_seconds
                                                    )}{' '}
                                                    Billable
                                                  </span>
                                                  <span className='text-[10px] tabular-nums text-muted-foreground'>
                                                    {formatSeconds(
                                                      user.duration_seconds -
                                                        user.billable_duration_seconds
                                                    )}{' '}
                                                    Non-Billable
                                                  </span>
                                                </div>
                                              </TableCell>

                                              {hasPageAccess('finance') && (
                                                <>
                                                  {/* Employee Cost */}
                                                  <TableCell className='px-4 py-2.5 text-center'>
                                                    <div className='text-sm font-medium tabular-nums'>
                                                      {formatCurrencyWithDash(
                                                        user.total_employee_cost
                                                      )}
                                                    </div>
                                                    <div className='flex flex-col gap-0.5 mt-0.5'>
                                                      <span className='text-[10px] tabular-nums text-muted-foreground'>
                                                        {formatCurrencyWithDash(
                                                          user.billable_employee_cost
                                                        )}{' '}
                                                        Billable
                                                      </span>
                                                      <span className='text-[10px] tabular-nums text-red-500'>
                                                        {formatCurrencyWithDash(
                                                          user.nonbillable_employee_cost
                                                        )}{' '}
                                                        Non-Bill.
                                                      </span>
                                                    </div>
                                                  </TableCell>

                                                  {/* Overhead */}
                                                  <TableCell className='px-4 py-2.5 text-center'>
                                                    <span className='text-sm font-medium tabular-nums text-amber-600 dark:text-amber-400'>
                                                      {formatCurrencyWithDash(
                                                        user.overhead_cost
                                                      )}
                                                    </span>
                                                  </TableCell>

                                                  {/* Fully Loaded */}
                                                  <TableCell className='px-4 py-2.5 text-center'>
                                                    <span className='text-sm font-semibold tabular-nums'>
                                                      {formatCurrencyWithDash(
                                                        user.fully_loaded_cost ??
                                                          (user.total_employee_cost ??
                                                            0) +
                                                            (user.overhead_cost ??
                                                              0)
                                                      )}
                                                    </span>
                                                  </TableCell>
                                                </>
                                              )}

                                              {/* Project Amount */}
                                              {(hasPageAccess('finance') ||
                                                hasPageAccess(
                                                  'mini_finance'
                                                )) && (
                                                <TableCell className='px-4 py-2.5 text-center'>
                                                  <div className='text-sm font-medium tabular-nums'>
                                                    {formatCurrencyWithDash(
                                                      user.total_project_cost
                                                    )}
                                                  </div>
                                                  <div className='text-[10px] tabular-nums text-emerald-600 dark:text-emerald-400 mt-0.5'>
                                                    {formatCurrencyWithDash(
                                                      user.billable_project_cost
                                                    )}{' '}
                                                    Billable
                                                  </div>
                                                </TableCell>
                                              )}
                                            </TableRow>
                                          )
                                        })
                                      ) : (
                                        <TableRow>
                                          <TableCell
                                            colSpan={
                                              hasPageAccess('finance')
                                                ? 6
                                                : hasPageAccess('mini_finance')
                                                  ? 3
                                                  : 2
                                            }
                                            className='h-20 text-center'
                                          >
                                            <p className='text-xs text-muted-foreground'>
                                              No resource logs for this
                                              milestone
                                            </p>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                              </motion.div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  })
                )}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>
      </div>
    </motion.div>
  )
}

export default Milestones
